import { Box, Stack } from '@mui/material';
import type { ReactNode } from 'react';
import type { OutcomeProperties, SpecialValue } from '../../@types';
import type { MarketDisplayType } from '../../types/enums';
import { mergeSx } from '../../utils/styles';
import { BetDetails } from '../atoms/BetDetails';

export type BetBuilderLegListItem = {
  id: string;
  outcomeId: string;
  outcomeName: string;
  outcomeShortName?: string;
  marketName: string;
  marketShortName?: string;
  marketTypeName: string;
  marketDisplayType?: MarketDisplayType;
  eventName: string;
  properties?: OutcomeProperties;
  specialValues?: SpecialValue[];
  sportId?: string;
};

const styles = {
  dotColors: (borderColor: string, backgroundColor: string) => ({ borderColor, backgroundColor }),
  connectorColor: (backgroundColor: string) => ({ backgroundColor }),
  color: (color: string) => ({ color }),
  leg: {
    alignItems: 'flex-start',
    py: 0.5,
  },
  legRail: {
    position: 'relative',
    width: 14,
    flexShrink: 0,
    alignSelf: 'stretch',
    display: 'flex',
    justifyContent: 'center',
    pt: '5px',
  },
  legDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    border: '1.5px solid',
    borderColor: 'neutral.300',
    backgroundColor: 'info.main',
    position: 'relative',
    zIndex: 1,
  },
  legConnector: {
    position: 'absolute',
    top: '10px',
    bottom: '-20px',
    left: '50%',
    width: '1px',
    transform: 'translateX(-50%)',
    backgroundColor: 'neutral.300',
  },
  legDetails: {
    flex: 1,
    minWidth: 0,
    alignItems: 'start',
  },
} as const;

export type BetBuilderLegListVariant = 'default' | 'neobet' | 'gola';

const VARIANT_COLORS: Record<
  BetBuilderLegListVariant,
  {
    outcomeColor: string;
    marketColor: string;
    dotColor: string;
    dotBorderColor: string;
    connectorColor: string;
  }
> = {
  default: {
    outcomeColor: 'neutral.600',
    marketColor: 'neutral.600',
    dotColor: 'info.main',
    dotBorderColor: 'neutral.300',
    connectorColor: 'neutral.300',
  },
  neobet: {
    outcomeColor: 'neutral.50',
    marketColor: 'neutral.200',
    dotColor: 'info.200',
    dotBorderColor: 'neutral.300',
    connectorColor: 'neutral.400',
  },
  gola: {
    outcomeColor: 'neutral.800',
    marketColor: 'neutral.600',
    dotColor: 'info.main',
    dotBorderColor: 'neutral.300',
    connectorColor: 'neutral.300',
  },
};

type BetBuilderLegListProps = {
  legs: BetBuilderLegListItem[];
  renderAction?: (leg: BetBuilderLegListItem) => ReactNode;
  variant?: BetBuilderLegListVariant;
  outcomeColor?: string;
  marketColor?: string;
  dotColor?: string;
  dotBorderColor?: string;
  connectorColor?: string;
};

const BetBuilderLegList = ({
  legs,
  renderAction,
  variant = 'default',
  outcomeColor,
  marketColor,
  dotColor,
  dotBorderColor,
  connectorColor,
}: BetBuilderLegListProps) => {
  const variantColors = VARIANT_COLORS[variant];
  const resolvedOutcomeColor = outcomeColor ?? variantColors.outcomeColor;
  const resolvedMarketColor = marketColor ?? variantColors.marketColor;
  const resolvedDotColor = dotColor ?? variantColors.dotColor;
  const resolvedDotBorderColor = dotBorderColor ?? variantColors.dotBorderColor;
  const resolvedConnectorColor = connectorColor ?? variantColors.connectorColor;

  return (
    <Stack data-testid='bet-builder-leg-list'>
      {legs.map((leg, index) => {
        const isLastSelection = index === legs.length - 1;
        const specialValue = leg.specialValues?.[0];

        return (
          <Stack key={leg.id} direction='row' spacing={1} data-testid='bet-builder-leg' sx={styles.leg}>
            <Box sx={styles.legRail}>
              <Box
                sx={mergeSx(styles.legDot, styles.dotColors(resolvedDotBorderColor, resolvedDotColor))}
                data-testid='bet-builder-leg-dot'
              />
              {!isLastSelection && (
                <Box
                  sx={mergeSx(styles.legConnector, styles.connectorColor(resolvedConnectorColor))}
                  data-testid='bet-builder-leg-connector'
                />
              )}
            </Box>
            <Stack sx={styles.legDetails}>
              <BetDetails.Outcome
                outcomeName={leg.outcomeName}
                outcomeShortName={leg.outcomeShortName}
                {...leg.properties}
                marketName={leg.marketName}
                marketTypeName={leg.marketTypeName}
                marketDisplayType={leg.marketDisplayType}
                eventName={leg.eventName}
                variant='subtitle2'
                sx={styles.color(resolvedOutcomeColor)}
              />
              <BetDetails.Market
                marketName={leg.marketName}
                marketShortName={leg.marketShortName}
                marketType={leg.marketTypeName}
                sx={styles.color(resolvedMarketColor)}
                variant='body3'
              />
              {!!leg.outcomeShortName && !!leg.specialValues?.length && (
                <BetDetails.SpecialValues
                  outcomeName={leg.outcomeName}
                  specialValues={leg.specialValues}
                  marketTypeName={leg.marketTypeName}
                  specialValueModel={specialValue?.model}
                  sportId={leg.sportId ?? ''}
                  sx={styles.color(resolvedMarketColor)}
                  variant='body3'
                />
              )}
            </Stack>
            {renderAction?.(leg)}
          </Stack>
        );
      })}
    </Stack>
  );
};

export default BetBuilderLegList;
